import { FC, useEffect, useMemo } from 'react';
import { Avatar, Col, Row, Skeleton, Typography } from 'antd';
import { TbChartInfographic, TbDashboard, TbReportAnalytics } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { PlatformForm as Form, PlatformSelect as Select } from '@/shared/components/forms';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import { useDashboardStore } from '@/stores';

export interface AlertReportDestination {
    dashboardId: string;
    reportId: string;
}

interface AlertReportDestinationSectionProps {
    onChange: (value?: AlertReportDestination) => void;
    value?: AlertReportDestination;
}

const AlertReportDestinationSection: FC<AlertReportDestinationSectionProps> = ({ onChange, value }) => {
    const { token } = useAntDesignTheme();
    const { dashboards, fetchDashboardById, fetchDashboards, loading, loadingDashboard, selectedDashboard } =
        useDashboardStore();
    const dashboard = useMemo(
        () =>
            selectedDashboard?.id === value?.dashboardId
                ? selectedDashboard
                : dashboards.find((item) => item.id === value?.dashboardId),
        [dashboards, selectedDashboard, value?.dashboardId],
    );
    const reports = dashboard?.reports ?? [];

    useEffect(() => {
        void fetchDashboards();
    }, [fetchDashboards]);

    useEffect(() => {
        if (value?.dashboardId) {
            void fetchDashboardById(value.dashboardId);
        }
    }, [fetchDashboardById, value?.dashboardId]);

    return (
        <section
            id='alert-report-component-destination'
            tabIndex={-1}
            style={{
                border: `1px dashed ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                marginTop: '0.75rem',
                outline: 'none',
                padding: '0.75rem',
            }}
        >
            <Row align='middle' gutter={[8, 0]} wrap={false}>
                <Col flex='none'>
                    <Avatar
                        shape='square'
                        icon={<TbChartInfographic />}
                        style={{
                            backgroundColor: token.colorFillSecondary,
                            border: `1px solid ${token.colorBorder}`,
                            color: token.colorTextTertiary,
                        }}
                    />
                </Col>
                <Col flex='auto' style={{ minWidth: 0 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                        Report Component Destination
                    </Typography.Title>
                    <small style={{ color: token.colorTextTertiary }}>
                        Select the governed dashboard and report that will own the generated component.
                    </small>
                </Col>
            </Row>

            {loading && dashboards.length === 0 ? (
                <Skeleton active paragraph={{ rows: 2 }} style={{ marginTop: '0.75rem' }} title={false} />
            ) : dashboards.length === 0 ? (
                <EmptySection
                    description='Create a dashboard and report before enabling Report Component delivery.'
                    height='11rem'
                    icon={<TbChartInfographic />}
                    label='No Reports Available'
                />
            ) : (
                <Row gutter={12} style={{ marginTop: '0.75rem' }}>
                    <Col span={12}>
                        <Form.Item label='Dashboard' required style={{ marginBottom: 0 }}>
                            <Select
                                onChange={(dashboardId) => {
                                    onChange(dashboardId ? { dashboardId, reportId: '' } : undefined);
                                }}
                                options={dashboards.map((item) => ({
                                    icon: <TbDashboard />,
                                    label: item.name || 'Untitled Dashboard',
                                    value: item.id,
                                }))}
                                placeholder='Select Dashboard...'
                                showSearch
                                value={value?.dashboardId || undefined}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label='Report' required style={{ marginBottom: 0 }}>
                            <Select
                                disabled={!value?.dashboardId}
                                loading={loadingDashboard}
                                onChange={(reportId) => {
                                    if (value?.dashboardId && reportId) {
                                        onChange({ dashboardId: value.dashboardId, reportId });
                                    }
                                }}
                                options={reports.map((report) => ({
                                    icon: <TbReportAnalytics />,
                                    label: report.name || 'Untitled Report',
                                    value: report.id,
                                }))}
                                placeholder={value?.dashboardId ? 'Select Report...' : 'Select A Dashboard First...'}
                                showSearch
                                value={value?.reportId || undefined}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            )}
        </section>
    );
};

export default AlertReportDestinationSection;
